// Pure helper used by the Surfaces tab to decide which sample points should
// decorate a given 2-D slice of a higher-dimensional campaign. A point is "on
// slice" iff every input that isn't mapped to the x/y axes falls within
// `slabFraction * range(input)` of the slice's pinned value.
//
// Unit tests for this helper live in `SurfacesTab.test.tsx` (kept there so
// this file remains free of test-time dependencies).

import type { AxisSpec, SamplePoint, SliceState } from '../types';

/**
 * Return the subset of `points` whose non-(x,y) inputs lie within the
 * slice's slab. Fixed-mode inputs are skipped (they are implicitly on-slice
 * for every sample).
 */
export function projectPointsToSlice(
  points: readonly SamplePoint[],
  slice: SliceState,
  inputs: readonly AxisSpec[],
): SamplePoint[] {
  const { xInput, yInput, pinned, slabFraction } = slice;

  // Pre-compute the tolerance per named axis (absolute, in axis units).
  const toleranceByName: Record<string, number> = {};
  for (const axis of inputs) {
    if (axis.mode !== 'range') continue;
    if (axis.name === xInput || axis.name === yInput) continue;
    if (axis.min === undefined || axis.max === undefined) continue;
    const range = axis.max - axis.min;
    toleranceByName[axis.name] = Math.max(0, slabFraction) * range;
  }

  return points.filter((p) => {
    for (const axis of inputs) {
      if (axis.mode !== 'range') continue;
      if (axis.name === xInput || axis.name === yInput) continue;
      const pivot = pinned[axis.name];
      if (pivot === undefined) continue;
      const value = p.inputs[axis.name];
      if (value === undefined) return false;
      const tol = toleranceByName[axis.name] ?? 0;
      if (Math.abs(value - pivot) > tol) return false;
    }
    return true;
  });
}
